import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_NAME = "Still Form";
const SITE_URL = "https://stillformworld.com";
const DEFAULT_DESC =
  "Still Form — a quiet editorial on ritual recovery, movement, sensory living, and the evidence-based return to ancient wisdom.";
const DEFAULT_OG_IMAGE = `${SITE_URL}/generated/hero-hero-a1bc74.png`;

function abs(url) {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${SITE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}

/**
 * Seo — per-page <title>, meta description, canonical, Open Graph, Twitter card,
 * and optional JSON-LD structured data. Drop one at the top of any page.
 *
 * Props:
 *  - title         page-specific title (auto-suffixed with " — Still Form")
 *  - description   meta description (≤ 160 chars)
 *  - path          canonical path, e.g. "/journal/foo"  (omit query strings)
 *  - image         OG/Twitter cover image (relative or absolute)
 *  - type          "website" | "article" | "product" (default: website)
 *  - jsonLd        optional object — will be JSON.stringified into <script type="application/ld+json">
 *  - noindex       boolean — emits <meta name="robots" content="noindex,nofollow">
 */
export default function Seo({
  title,
  description,
  path = "/",
  image,
  type = "website",
  jsonLd,
  noindex = false,
}) {
  const fullTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — Live slowly, in form.`;
  const desc = description || DEFAULT_DESC;
  const canonical = `${SITE_URL}${path}`;
  const ogImage = abs(image) || DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type === "article" ? "article" : type === "product" ? "product" : "website"} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

export { SITE_URL, SITE_NAME };
